import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowRight } from "lucide-react";

// ============================================================================
//  BlogCalendarOptin — calm, OCEAN-styled, post-benediction opt-in card.
//  Lives at the very bottom of every blog post (Blog.js renders it after
//  the closing/benediction, related posts, and any book CTA).
//
//  Copy is verbatim from Temple. Don't redesign the headline.
//
//  Behavior:
//   - Posts to /api/subscribe (uses MAILERLITE_DEFAULT_GROUP_ID = calendar group)
//   - Fires GA4 `optin_submit` + `generate_lead`
//   - Fires Pinterest Tag `lead` conversion
//   - Redirects to /start-success on success (same as /start)
// ============================================================================

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OCEAN = {
  deep: "#1B6B6E",
  mid: "#3D9096",
  light: "#7BC8CB",
  cream: "#F4F1EC",
  mint: "#EBF6F6",
  dark: "#1C2B2B",
};

export default function BlogCalendarOptin({
  articleSlug = "blog",
  headline = "Before you go, dear one —",
  body = "Get the free 12-Month I GET TO Calendar — a daily I GET TO prompt for every day of the year, plus my weekly walk-with-me emails.",
  buttonText = "Send me the free calendar",
  microcopy = "No spam. Unsubscribe anytime. 🤍",
}) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API_URL}/api/subscribe`, {
        firstName: firstName || "Friend",
        email: email.trim().toLowerCase(),
        group_id: "186110662635684873", // Tap-in 12 month Calendar group — explicit
        source: `blog-post-${articleSlug}`,
      });

      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "optin_submit", {
          method: "blog_calendar_card",
          source_page: articleSlug,
          value: 0,
          currency: "USD",
        });
        window.gtag("event", "generate_lead", { currency: "USD", value: 0 });
      }
      // Meta Pixel — Lead conversion (safe no-op until pixel base loads)
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: "Free Calendar (blog)", content_category: "lead-magnet", value: 0, currency: "USD" });
      }
      // Mark this device as a returning subscriber so homepage swaps to the "welcome back" hero
      try { localStorage.setItem("mbsh_subscribed", "true"); } catch (_) { /* ignore */ 
      }
      if (typeof window !== "undefined" && typeof window.pintrk === "function") {
        window.pintrk("track", "lead", { lead_type: "email_optin" });
      }

      navigate("/start-success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail || "Something hiccuped. Try once more, Lovely."
      );
    }
  };

  return (
    <div
      className="rounded-3xl px-6 md:px-10 py-10 md:py-12 mb-10"
      style={{ background: OCEAN.mint, border: `1px solid ${OCEAN.light}40` }}
      data-testid="blog-calendar-optin-card"
    >
      <p
        className="font-serif italic text-lg md:text-xl leading-snug mb-4"
        style={{ color: OCEAN.deep }}
        data-testid="blog-calendar-optin-greeting"
      >
        {headline}
      </p>

      <p
        className="font-sans text-base md:text-lg leading-relaxed mb-7 max-w-2xl"
        style={{ color: OCEAN.dark }}
        data-testid="blog-calendar-optin-body"
      >
        {body}
      </p>

      <form
        onSubmit={handleSubmit}
        className="space-y-3 max-w-md"
        data-testid="blog-calendar-optin-form"
      >
        <input
          type="text"
          placeholder="First name (optional)"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="w-full px-5 py-3.5 rounded-full font-sans text-base focus:outline-none focus:ring-2 transition-all"
          style={{
            background: "white",
            color: OCEAN.dark,
            border: `1.5px solid ${OCEAN.light}80`,
          }}
          data-testid="blog-calendar-firstname-input"
        />
        <input
          type="email"
          placeholder="Your best email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full px-5 py-3.5 rounded-full font-sans text-base focus:outline-none focus:ring-2 transition-all"
          style={{
            background: "white",
            color: OCEAN.dark,
            border: `1.5px solid ${OCEAN.light}80`,
          }}
          data-testid="blog-calendar-email-input"
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-sans font-semibold tracking-wide transition-all hover:-translate-y-0.5 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: OCEAN.deep, color: OCEAN.cream }}
          data-testid="blog-calendar-submit-btn"
        >
          {status === "submitting" ? (
            "Sending…"
          ) : (
            <>
              <Mail className="w-4 h-4" />
              {buttonText}
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
        {errorMsg && (
          <p
            className="font-sans text-sm pt-1"
            style={{ color: "#9B1C1C" }}
            data-testid="blog-calendar-error-msg"
          >
            {errorMsg}
          </p>
        )}
      </form>

      <p
        className="font-sans text-xs mt-4"
        style={{ color: OCEAN.deep, opacity: 0.6 }}
        data-testid="blog-calendar-microcopy"
      >
        {microcopy}
      </p>
    </div>
  );
}
